import type { Option } from "./option.ts";

/**
 * `Internals` — a class's friend record, reached through its instances.
 *
 * A class whose `#` members another module needs (a facade's `dispose`, a
 * client's boot `seed`, an element's test instrumentation) keeps ONE record
 * of functions over its instances in a `static readonly #internals` field,
 * and its constructor registers each new instance against it:
 *
 *     const facadeInternals = new Internals<Facade, FacadeInternals>("Facade");
 *
 *     export function disposeFacade(facade: Facade): void {
 *       facadeInternals.of(facade).dispose(facade);
 *     }
 *
 *     class Facade {
 *       static readonly #internals: FacadeInternals = { dispose: (facade) => facade.#dispose() };
 *       constructor() {
 *         facadeInternals.register(this, Facade.#internals);
 *       }
 *     }
 *
 * The registry is the module's only route to the record: nothing else holds
 * it, so the friend door is a data structure keyed by the instance rather
 * than a module-level `let` that a class static block assigns. The record is shared
 * by every instance of the class (its functions take the instance), so an
 * instance costs one weak entry and no closures. A decorated class (a custom
 * element's `@component`) cannot hold a static `#` field, so its constructor
 * passes the record literal itself.
 *
 * `of(x)` throws a `TypeError` for an object no constructor of the class
 * registered — what reading a `#` member of a foreign object throws; `peek(x)`
 * answers `null` for it (a seam that also answers for an unconstructed
 * prototype instance). A registry is module-private: it is never exported,
 * and `tools/lint/module-state.deno.test.ts` admits a module-level
 * `new Internals` only when every `register` call in its module passes `this`.
 */
export class Internals<Owner extends object, Friend extends object> {
  readonly #owner: string;
  readonly #records = new WeakMap<Owner, Friend>();

  /** A registry for the instances of the class named `owner` (named in its errors). */
  constructor(owner: string) {
    this.#owner = owner;
  }

  /** Register `instance` with its class's `record`: once, from the class's constructor. */
  register(instance: Owner, record: Friend): void {
    if (this.#records.has(instance)) {
      throw new Error(`${this.#owner}: an instance registered its internals twice`);
    }
    this.#records.set(instance, record);
  }

  /** `instance`'s record; a `TypeError` for an object its class's constructor never registered. */
  of(instance: Owner): Friend {
    const record = this.#records.get(instance);
    if (record === undefined) {
      throw new TypeError(`${this.#owner}: not a constructed ${this.#owner} (no internals registered)`);
    }
    return record;
  }

  /** `instance`'s record, or `null` for an object no constructor registered (an unconstructed prototype instance). */
  peek(instance: Owner): Option<Friend> {
    return this.#records.get(instance) ?? null;
  }
}
